// URL-fragment parsing for account-mode deep links. The Desktop Runtime
// endpoint/token credential lane this module used to own is retired; legacy
// `endpoint`/`token` fragments are stripped by canonicalizeLegacyHashRoute()
// rather than parsed here.

export interface ParsedFragment {
  route?: string;
  source?: string;
  /** Active run id — lets a created run's status survive reload (account mode). */
  run_id?: string;
  /** Force the "Run on" placement screen (skip a saved launch binding / attach),
   *  so the user can choose a placement (e.g. Managed Cloud) for `source`. */
  pick?: boolean;
  /** Free-text search query carried to Discover (hero search / intent preset). */
  q?: string;
  waitlist?: "cloud";
  waitlist_source?: "pwa_home" | "pwa_desktop" | "ato_web";
}

export function parseFragment(): ParsedFragment {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return {};
  const params = new URLSearchParams(hash);
  // Normalize route to always carry a leading slash so bare forms like
  // `route=run` resolve identically to `route=/run`. All route comparisons in
  // the app use the slashed form (`route === "/run"` etc.); without this,
  // handoff/share URLs that omit the slash silently fall through to the
  // dashboard instead of the intended screen.
  const rawRoute = params.get("route") ?? undefined;
  const route =
    rawRoute && !rawRoute.startsWith("/") ? `/${rawRoute}` : rawRoute;
  const rawWaitlist = params.get("waitlist");
  const rawWaitlistSource = params.get("waitlist_source");
  return {
    route,
    source: params.get("source") ?? undefined,
    run_id: params.get("run_id") ?? undefined,
    pick: params.get("pick") === "1" || params.get("pick") === "true",
    q: params.get("q") ?? undefined,
    waitlist: rawWaitlist === "cloud" ? rawWaitlist : undefined,
    waitlist_source:
      rawWaitlistSource === "pwa_home" ||
      rawWaitlistSource === "pwa_desktop" ||
      rawWaitlistSource === "ato_web"
        ? rawWaitlistSource
        : undefined,
  };
}

export function clearWaitlistIntent(): void {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  if (!params.has("waitlist") && !params.has("waitlist_source")) return;
  params.delete("waitlist");
  params.delete("waitlist_source");
  const hash = params.toString();
  history.replaceState(
    null,
    "",
    window.location.pathname +
      window.location.search +
      (hash ? `#${hash}` : ""),
  );
}
